import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createSelector } from '@reduxjs/toolkit';
import { Grid3X3, List, Loader2, Search, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { setViewMode } from '../../redux/slices/uiSlice';
import { openSearchModal } from '../../redux/slices/userDocumentSearchSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DBDocumentThunks } from '../../redux/thunks/DBServiceThunks';
import DocumentUploadDialog from './document-upload-dialog';
import DocumentsEmptyState from './documents-empty-state';
import DocumentsGrid from './documents-grid';
import NoKnowledgebaseState from './no-knowledgebase-state';

type ViewMode = 'grid' | 'list';

const selectKnowledgebase = createSelector(
    [(state: any) => state.DBKnowledgebaseReducer.entities, (state: any) => state.CaseReducer.currentCaseId],
    (entities, currentCaseId) => {
        return Object.values(entities).find((kb: any) => kb.caseId === currentCaseId) as any;
    }
);

const selectDocuments = createSelector(
    [(state: any) => state.DBDocumentReducer.entities, (state: any, kbId?: string) => kbId],
    (entities, kbId) => {
        if (!kbId) return [];
        return Object.values(entities).filter((doc: any) => doc.knowledgebaseId === kbId) as any[];
    }
);

export default function KnowledgebaseContent() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const currentCaseId = useAppSelector((state) => state.CaseReducer.currentCaseId);
    const knowledgebase = useAppSelector(selectKnowledgebase);
    const documents = useAppSelector((state) => selectDocuments(state, knowledgebase?._id));
    const viewMode = useAppSelector((state) => state.UIReducer.viewMode as ViewMode);

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleViewModeChange = (mode: ViewMode) => {
        dispatch(setViewMode(mode));
    };

    const handleUploadDialogOpen = () => {
        setUploadDialogOpen(true);
    };

    const handleUploadDialogClose = () => {
        setUploadDialogOpen(false);
    };

    useEffect(() => {
        if (currentCaseId) {
            dispatch(DBDocumentThunks.find({ caseId: currentCaseId }));
        }
    }, [currentCaseId, dispatch]);

    // Handle search loading state
    useEffect(() => {
        if (searchTerm || knowledgebase) {
            setIsLoading(true);
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [searchTerm, knowledgebase]);

    if (!knowledgebase) return <NoKnowledgebaseState />;

    return (
        <div
            dir={getLanguageDirection(i18n.language)}
            className="px-2 text-start h-full flex flex-col"
        >
            {/* Search & Filter Bar */}
            <div className="py-3 flex gap-2 items-center">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t('knowledgebase.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 rounded-lg"
                    />
                </div>

                {/* Smart Search Button */}
                <Button
                    variant="default"
                    size="sm"
                    onClick={() => dispatch(openSearchModal({ isSearchAll: true }))}
                    className="gap-2 bg-gold hover:bg-gold-bright text-white border-none shadow-md"
                >
                    <Sparkles className="h-4 w-4" />
                    <span className="hidden sm:inline">
                        {t('knowledgebase.smartSearch', 'Smart Search')}
                    </span>
                </Button>

                <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => handleViewModeChange('grid')}
                    className="h-9 w-9 p-0"
                >
                    <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => handleViewModeChange('list')}
                    className="h-9 w-9 p-0"
                >
                    <List className="h-4 w-4" />
                </Button>
            </div>

            {/* Loading indicator */}
            {isLoading ? (
                <div className="flex justify-center items-center flex-1 h-full">
                    <Loader2 size={32} className="animate-spin text-gold" />
                </div>
            ) : (
                <div className="flex-1 overflow-auto flex flex-col">
                    {documents && documents.length > 0 ? (
                        <DocumentsGrid
                            knowledgebaseId={knowledgebase._id}
                            onUploadClick={handleUploadDialogOpen}
                            searchTerm={searchTerm}
                        />
                    ) : (
                        <DocumentsEmptyState
                            onUploadClick={handleUploadDialogOpen}
                        />
                    )}
                </div>
            )}

            <DocumentUploadDialog
                open={uploadDialogOpen}
                onClose={handleUploadDialogClose}
                caseId={currentCaseId}
                knowledgebaseId={knowledgebase._id}
            />
        </div>
    );
}
